'use strict'

const { moveListItem } = require('./list-move')

function pageTreeFromPages (pages) {

  const list = Array.isArray(pages) ? pages : []
  const byTitle = new Map()

  for (const page of list) {

    if (!page || !page.uniqueTitle) continue

    page.children = []
    byTitle.set(page.uniqueTitle, page)
  }

  const roots = []

  for (const page of list) {

    if (!page || !page.uniqueTitle) continue

    const parent = page.parentUniqueTitle && byTitle.get(page.parentUniqueTitle)

    if (!parent || parent === page || parentChainContains(parent, page, byTitle)) {

      roots.push(page)
      continue
    }

    parent.children.push(page)
  }

  return roots
}

function parentChainContains (parent, page, byTitle) {

  const seen = new Set()
  let current = parent

  while (current) {

    if (current === page || current.uniqueTitle === page.uniqueTitle) return true

    if (seen.has(current.uniqueTitle)) return true

    seen.add(current.uniqueTitle)

    const nextTitle = current.parentUniqueTitle

    current = nextTitle ? byTitle.get(nextTitle) : null
  }

  return false
}

function pagePlacementFromTree (roots) {

  const pages = []
  const seen = new Set()

  function walk (nodes, parentUniqueTitle) {

    for (const node of nodes || []) {

      if (!node || !node.uniqueTitle) continue

      if (seen.has(node.uniqueTitle)) {

        return `page ${node.uniqueTitle} cannot be nested under itself`
      }

      seen.add(node.uniqueTitle)
      pages.push({
        uniqueTitle: node.uniqueTitle,
        parentUniqueTitle: parentUniqueTitle || null
      })

      const error = walk(node.children, node.uniqueTitle)

      if (error) return error
    }

    return null
  }

  const error = walk(roots, null)

  if (error) return { error }

  return { pages }
}

function normalizePageArrangement (knownUniqueTitles, orderedPages) {

  if (!Array.isArray(orderedPages)) {

    return { error: 'pages required' }
  }

  const known = new Set(knownUniqueTitles || [])
  const seen = new Set()
  const parentOf = new Map()
  const pages = []

  for (const item of orderedPages) {

    const uniqueTitle = item && item.uniqueTitle

    if (!uniqueTitle || !known.has(uniqueTitle)) {

      return { error: `page ${uniqueTitle || ''} not found` }
    }

    if (seen.has(uniqueTitle)) {

      return { error: `page ${uniqueTitle} is listed more than once` }
    }

    seen.add(uniqueTitle)

    const parentUniqueTitle = item.parentUniqueTitle || null

    if (parentUniqueTitle) {

      if (!known.has(parentUniqueTitle)) {

        return { error: `page ${parentUniqueTitle} not found` }
      }

      if (parentUniqueTitle === uniqueTitle) {

        return { error: `page ${uniqueTitle} cannot be its own parent` }
      }
    }

    parentOf.set(uniqueTitle, parentUniqueTitle)
    pages.push({ uniqueTitle, parentUniqueTitle })
  }

  for (const uniqueTitle of known) {

    if (!seen.has(uniqueTitle)) {

      return { error: `page ${uniqueTitle} is missing from the arrangement` }
    }
  }

  for (const uniqueTitle of seen) {

    const walked = new Set()
    let current = parentOf.get(uniqueTitle)

    while (current) {

      if (current === uniqueTitle || walked.has(current)) {

        return { error: `page ${uniqueTitle} cannot be nested under itself` }
      }

      walked.add(current)
      current = parentOf.get(current) || null
    }
  }

  return { pages }
}

function findPageSiblings (roots, uniqueTitle) {

  function walk (nodes, parent) {

    const list = nodes || []
    const index = list.findIndex(node => node && node.uniqueTitle === uniqueTitle)

    if (index >= 0) return { list, index, parent }

    for (const node of list) {

      const found = walk(node && node.children, node)

      if (found) return found
    }

    return null
  }

  return walk(roots, null)
}

function movePageInTree (roots, uniqueTitle, direction) {

  const found = findPageSiblings(roots, uniqueTitle)

  if (!found) return false

  if (direction === 'out') {

    if (!found.parent) return false

    const parentFound = findPageSiblings(roots, found.parent.uniqueTitle)

    if (!parentFound) return false

    const item = found.list.splice(found.index, 1)[0]

    parentFound.list.splice(parentFound.index + 1, 0, item)

    return true
  }

  return moveListItem(found.list, found.index, direction)
}

function applyPageParents (pages, arranged) {

  const byTitle = new Map()

  ;(arranged || []).forEach((item, index) => {

    if (item && item.uniqueTitle) byTitle.set(item.uniqueTitle, index)
  })

  for (const page of pages || []) {

    if (!page || !byTitle.has(page.uniqueTitle)) continue

    const index = byTitle.get(page.uniqueTitle)
    const item = arranged[index]

    page.sortOrder = index

    if (item.parentUniqueTitle) page.parentUniqueTitle = item.parentUniqueTitle
    else delete page.parentUniqueTitle
  }
}

module.exports = {
  pageTreeFromPages,
  pagePlacementFromTree,
  normalizePageArrangement,
  applyPageParents,
  movePageInTree
}
